import { useState } from "react";
import axios from "axios";
import { toast } from "@/components/ui/sonner";
import { Reveal } from "@/components/motion";
import { ArrowUpRight, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-card border border-border px-4 py-3.5 text-sm font-sans focus:outline-none focus:border-foreground transition-colors duration-200 placeholder:text-muted-foreground";

const AccessSection = () => {
  const [form, setForm] = useState({ name: "", email: "", company: "", use_case: "" });
  const [submitting, setSubmitting] = useState(false);
  const [refId, setRefId] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/access/request`, form);
      setRefId(res.data.request_id);
      toast.success("Access request received");
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || "Submission failed — check your details");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="request-access" data-testid="request-access" className="border-b border-border grain-overlay relative">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 lg:py-32 grid lg:grid-cols-12 gap-12 relative z-10">
        <div className="lg:col-span-6">
          <Reveal>
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-telemetry mb-6">11 · Request Access</p>
            <h2 className="font-display font-black tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
              Get liability-grade truth on your network.
            </h2>
            <p className="mt-6 text-base md:text-lg text-muted-foreground leading-relaxed max-w-md">
              We onboard a limited number of carriers, platforms, and insurers each quarter. Tell us
              about your operation and we&apos;ll respond within two business days.
            </p>
          </Reveal>
        </div>
        <div className="lg:col-span-6">
          <Reveal delay={0.15}>
            {refId ? (
              <div data-testid="access-success" className="border border-truth p-10 bg-card">
                <CheckCircle2 className="w-8 h-8 text-truth mb-6" />
                <h3 className="font-display font-extrabold tracking-tight text-3xl">Request received.</h3>
                <p className="text-muted-foreground mt-3 text-sm leading-relaxed">
                  Your reference ID is logged in our intake system. Keep it for your records.
                </p>
                <p className="font-mono text-sm mt-6 bg-muted px-4 py-3 inline-block">{refId}</p>
                <div>
                  <button
                    data-testid="access-submit-another-btn"
                    onClick={() => { setRefId(null); setForm({ name: "", email: "", company: "", use_case: "" }); }}
                    className="mt-8 font-mono text-[11px] tracking-[0.2em] uppercase text-muted-foreground hover:text-foreground transition-colors duration-200 flex items-center gap-2"
                  >
                    Submit another request <ArrowUpRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ) : (
              <form data-testid="access-form" onSubmit={submit} className="border border-border bg-card p-8 lg:p-10 space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="access-name" className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground block mb-2">Full Name</label>
                    <input id="access-name" data-testid="access-name-input" required value={form.name} onChange={set("name")} className={inputCls} placeholder="Matt Reeves" />
                  </div>
                  <div>
                    <label htmlFor="access-email" className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground block mb-2">Work Email</label>
                    <input id="access-email" data-testid="access-email-input" required type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="matt@carrier.com" />
                  </div>
                </div>
                <div>
                  <label htmlFor="access-company" className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground block mb-2">Company</label>
                  <input id="access-company" data-testid="access-company-input" required value={form.company} onChange={set("company")} className={inputCls} placeholder="Delta Last-Mile Logistics" />
                </div>
                <div>
                  <label htmlFor="access-usecase" className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground block mb-2">Use Case</label>
                  <textarea id="access-usecase" data-testid="access-usecase-input" rows={4} value={form.use_case} onChange={set("use_case")} className={`${inputCls} resize-none`} placeholder="e.g. Verify 40k daily grocery deliveries across 3 routing engines; need insurer-ready audit trails." />
                </div>
                <button
                  data-testid="access-submit-btn"
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-foreground text-background font-mono text-xs tracking-[0.25em] uppercase px-8 py-4 hover:bg-telemetry transition-colors duration-200 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {submitting ? "Submitting…" : "Request Access"} <ArrowUpRight className="w-4 h-4" />
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default AccessSection;
